import { type FC, useMemo, useState, useCallback, useRef, useEffect } from "react";

import {
  ArrowRight,
  Check,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  Gavel,
  Home,
  Landmark,
  Loader2,
  Lock,
  Users,
  X,
} from "lucide-react";
import { enqueueSnackbar } from "notistack";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import { EstateExpertWidget } from "@/pages/postloss/overview/components/EstateExpertWidget";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { supabase } from "@/shared/lib/supabase";
import { useAdminTaskAnswers } from "@/shared/hooks/useAdminTaskAnswers";
import { useAdminStepSave } from "@/shared/hooks/useAdminStepSave";
import { useFlowSection } from "@/shared/hooks/useFlowSection";
import { trackEvent, EventType } from "@/shared/utils/core";
import { TOAST } from "@/shared/constants/toastMessages";

import type { PaymentTier } from "@/shared/utils/tierOrder";
import type { FlowSection } from "@/shared/constants/flowTypes";

import { AuthorityResultsCard } from "./AuthorityResultsCard";
import { KeyDetailsCard } from "./KeyDetailsCard";
import { InfoRow } from "./InfoRow";
import { KnowRow } from "./KnowRow";
import { ADMIN_TASK_IDS } from "@/shared/constants/taskIds";

import { ADMIN_STEPS } from "../administrationSteps";
import { AdminResultsScreen } from "./steps/AdminResultsScreen";
import { buildAdminScreensForTask } from "../tasks";

import paywallBlurredBg from "@/shared/assets/get-authority/paywall-content-blurred.png";

const CONTENT = {
  heading: (name: string) => `${name}'s estate goes through administration`,
  description: (name: string) =>
    `${name} didn't leave a valid will, so state law decides who can be in charge of the estate. Banks won't let you close accounts or move money until the court appoints an administrator and gives you legal authority, the court paper that lets you act for ${name}.`,
  unpaidDescription: (name: string) =>
    `Because ${name} died without a will, the court follows state law to appoint an administrator. The court issues Letters of Administration to give you official authority.`,
  sidebarDescription: (name: string) =>
    `Because ${name} passed away without a valid will, the court must appoint an administrator and issue Letters of Administration so you can legally manage ${name}'s affairs.`,
  timeline: "Typically 2 to 6 months to get Letters of Administration",
  unpaidTimeline: "Typically 3 to 9 months to get official authority",
  filingFee: "$215 to $625, set by estate size",
  attorneyStatus: "Optional in NY: File yourself or request a referral",
  paywallTitle: "Unlock your administration roadmap",
  paywallChecklist: [
    { text: "Your court forms, filled in line by line" },
    { text: "A step-by-step filing roadmap for your county" },
    { text: "The attorney hotline and an Estate Expert session", helpline: true },
    { text: "Room to invite any attorney you're working with" },
  ],
};

const ADMIN_SCREEN_TITLES: Record<string, string> = {
  admin_family_triage_intro: "Review family members",
  admin_family_triage_marriages: "Marriages",
  admin_family_triage_children: "Children",
  admin_family_triage_extended: "Family",
  admin_family_triage_review: "Review",
  admin_family_triage_loading: "Review",
};

interface AdminRoadmapProps {
  estateId: number;
  decedentFirstName: string;
  county: string | null;
  countyLabel: string;
  isPaid: boolean;
  tier: PaymentTier | null;
  helplineEnabled: boolean;
  isFirstVisit: boolean;
  onDismissFirstVisit: () => void;
  familyCount: number;
  propertyCount: number;
  keyDocCount: number;
  keyDocTotal: number;
  attorneyName: string | null;
}

export const AdminRoadmap: FC<AdminRoadmapProps> = ({
  estateId,
  decedentFirstName,
  county,
  countyLabel,
  isPaid,
  tier,
  helplineEnabled,
  isFirstVisit,
  onDismissFirstVisit,
  familyCount,
  propertyCount,
  keyDocCount,
  keyDocTotal,
  attorneyName,
}) => {
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [activeFlow, setActiveFlow] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { taskRows, initialAnswers, isLoading } = useAdminTaskAnswers(estateId);
  const { saveStep, completeTask, resetTaskStatus, resetPendingSave } = useAdminStepSave();

  const sheetContentRef = useRef<HTMLDivElement>(null);
  const activeFlowRef = useRef(activeFlow);
  activeFlowRef.current = activeFlow;
  const flowAnswersRef = useRef<Record<string, string | string[]>>({});

  const completedTaskIds = useMemo(() => {
    const set = new Set<string>();
    for (const row of taskRows ?? []) {
      if (row.status === "completed") set.add(row.task_id);
    }
    return set;
  }, [taskRows]);

  const allStepsCompleted = useMemo(
    () => ADMIN_STEPS.every((step) => completedTaskIds.has(step.taskId)),
    [completedTaskIds],
  );

  const startedTaskIds = useMemo(() => {
    const set = new Set<string>();
    for (const row of taskRows ?? []) {
      if (row.status === "in_progress") set.add(row.task_id);
    }
    return set;
  }, [taskRows]);


  const activeScreens = useMemo(
    () => (activeFlow ? buildAdminScreensForTask(activeFlow) : []),
    [activeFlow],
  );

  const handleFlowComplete = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    const taskId = activeFlowRef.current;
    try {
      if (taskId) {
        await completeTask(taskId, flowAnswersRef.current);
        trackEvent(EventType.ADMIN_STEP_COMPLETED, { estate_id: estateId, step_id: taskId });

        if (taskId === ADMIN_TASK_IDS.FAMILY_TRIAGE) {
          const downstreamIds = [
            ADMIN_TASK_IDS.CONFIRM_ADMIN,
            ADMIN_TASK_IDS.FAMILY_SIGNOFFS,
            ADMIN_TASK_IDS.FORMS_PACKET,
          ];
          await Promise.all(
            downstreamIds
              .filter((id) => completedTaskIds.has(id) || startedTaskIds.has(id))
              .map((id) => resetTaskStatus(id)),
          );
        }
      }
      setActiveFlow(null);
    } catch (err) {
      console.error("Failed to complete task:", err);
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    } finally {
      setIsSaving(false);
      resetPendingSave();
    }
  }, [completeTask, resetTaskStatus, resetPendingSave, estateId, isSaving, completedTaskIds, startedTaskIds]);

  const initialStep = useMemo(() => {
    if (!activeScreens.length || !Object.keys(initialAnswers).length) return 0;

    const taskRow = taskRows?.find((r) => r.task_id === activeFlow);
    const isCompleted = taskRow?.status === "completed";

    const visibleScreens = activeScreens.filter((s) => {
      if (!s.shouldDisplay) return true;
      return s.shouldDisplay({ answers: initialAnswers, currentStep: 0 });
    });

    if (isCompleted) return Math.max(visibleScreens.length - 1, 0);

    let lastAnswered = 0;
    for (let i = 0; i < visibleScreens.length; i++) {
      const screen = visibleScreens[i];
      if (initialAnswers[screen.id] === "completed" || (screen.question && initialAnswers[screen.question.id])) {
        lastAnswered = i + 1;
      }
    }
    return Math.min(lastAnswered, visibleScreens.length - 1);
  }, [activeScreens, initialAnswers, activeFlow, taskRows]);

  const handleStepComplete = useCallback(
    async (
      screenId: string,
      questionId: string | undefined,
      answer: string | string[] | undefined,
      allAnswers: Record<string, string | string[]>,
    ) => {
      await saveStep(screenId, questionId, answer, allAnswers);

      if (screenId === "admin_confirm_admin_someone_else") {
        const email = allAnswers["admin_someone_else_email"] as string | undefined;
        const fullName = allAnswers["admin_someone_else_full_name"] as string | undefined;
        const note = allAnswers["admin_someone_else_note"] as string | undefined;
        if (email && fullName) {
          const { error: emailError } = await supabase.functions.invoke("communications/email/send", {
            body: {
              type: "invitation",
              to: email,
              data: {
                invitee_name: fullName,
                decedent_name: decedentFirstName,
                estate_id: estateId,
                personal_note: note ?? "",
              },
            },
          });
          if (emailError) {
            console.error("Failed to send notification:", emailError);
            enqueueSnackbar("Could not send the notification email. You can try again from Contacts.", { variant: "error" });
          } else {
            enqueueSnackbar(`We've notified ${fullName}`, { variant: "success" });
          }
        }
      }
    },
    [saveStep, decedentFirstName, estateId],
  );

  const flowSection = useMemo((): FlowSection => ({
    id: activeFlow ?? "administration",
    title: "Administration",
    screens: activeScreens,
    allowSaveAndClose: true,
    saveOnEachStep: true,
    onComplete: handleFlowComplete,
    onStepComplete: handleStepComplete,
  }), [activeFlow, activeScreens, handleStepComplete, handleFlowComplete]);

  const {
    currentStep: flowStep,
    totalSteps: flowTotal,
    currentScreen,
    answers: flowAnswers,
    errors: flowErrors,
    setAnswer: setFlowAnswer,
    goNext: flowGoNext,
    goBack: flowGoBack,
    goToScreen: flowGoToScreen,
    isLastStep: flowIsLast,
  } = useFlowSection({ section: flowSection, initialStep, initialAnswers });

  flowAnswersRef.current = flowAnswers;

  const handleCloseSheet = useCallback(() => {
    const taskId = activeFlowRef.current;
    if (taskId && currentScreen) {
      saveStep(currentScreen.id, undefined, undefined, flowAnswersRef.current).catch(() => {
        enqueueSnackbar("Your latest answers may not have saved.", { variant: "warning" });
      });
    }
    setActiveFlow(null);
  }, [currentScreen, saveStep]);

  useEffect(() => {
    if (Object.keys(flowErrors).length > 0 && sheetContentRef.current) {
      const firstError = sheetContentRef.current.querySelector("[data-field-error]");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [flowErrors]);

  const activeStepConfig = ADMIN_STEPS.find((s) => s.taskId === activeFlow);

  const isNextDisabled = useMemo(() => {
    if (!currentScreen) return false;
    if (currentScreen.isButtonDisabled) {
      return currentScreen.isButtonDisabled({ answers: flowAnswers, currentStep: flowStep });
    }
    return false;
  }, [currentScreen, flowAnswers, flowStep]);

  const handleStepClick = useCallback(
    (step: (typeof ADMIN_STEPS)[number]) => {
      onDismissFirstVisit();
      setActiveFlow(step.taskId);
    },
    [onDismissFirstVisit],
  );

  return (
    <MainLayout header="Get authorized" actions={<PageActions />}>
      <div className="flex flex-col lg:flex-row gap-8 py-8 items-start">
        <div className="flex-1 flex flex-col gap-6 min-w-0">
          {!isPaid && (
            <Card className="bg-white overflow-hidden">
              <CardContent className="pt-6 flex flex-col gap-6">
                <Tag color="green" icon={Check} className="w-fit">
                  Your results
                </Tag>
                <div className="flex flex-col gap-2">
                  <h2>{CONTENT.heading(decedentFirstName)}</h2>
                  <p className="text-sm text-secondary">
                    {CONTENT.unpaidDescription(decedentFirstName)}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="border-t border-primary" />
                  <InfoRow
                    icon={Landmark}
                    label="Courthouse"
                    value={`Surrogate's Court${county ? `, ${county} County` : ""}`}
                  />
                  <div className="border-t border-primary" />
                  <InfoRow icon={Clock} label="Timeline" value={CONTENT.unpaidTimeline} />
                  <div className="border-t border-primary" />
                  <InfoRow icon={DollarSign} label="Filing fee" value={CONTENT.filingFee} />
                  <div className="border-t border-primary" />
                  <InfoRow icon={Landmark} label="Attorney status" value={CONTENT.attorneyStatus} />
                </div>
              </CardContent>
            </Card>
          )}

          {isPaid && isFirstVisit && !allStepsCompleted && (
            <div className="flex flex-col gap-3">
              <Tag color="green" icon={Check} className="w-fit">
                Your results
              </Tag>
              <div className="flex flex-col gap-2">
                <h2>{CONTENT.heading(decedentFirstName)}</h2>
                <p className="text-sm text-secondary">
                  {CONTENT.description(decedentFirstName)}
                </p>
              </div>
            </div>
          )}

          {allStepsCompleted ? (
            <AdminResultsScreen
              answers={initialAnswers}
              onComplete={() => {}}
              setAnswer={() => {}}
              goToScreen={() => {}}
              errors={{}}
            />
          ) : isPaid ? (
            <>
              <div className="flex flex-col gap-1">
                <h3 className="font-semibold">Your roadmap to legal authority</h3>
                <p className="text-sm text-secondary">
                  Answer a few questions about the estate, and the court forms you need to file are prepared for you.
                </p>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="size-6 animate-spin text-brand" />
                </div>
              ) : (
              <div className="flex flex-col">
                {ADMIN_STEPS.map((step, idx) => {
                  const isCompleted = completedTaskIds.has(step.taskId);
                  const isStarted = startedTaskIds.has(step.taskId);
                  const isFirstIncomplete = !isCompleted && ADMIN_STEPS.every((prev) =>
                    prev.number < step.number
                      ? completedTaskIds.has(prev.taskId)
                      : true,
                  );
                  const isFirst = idx === 0;
                  const isLast = idx === ADMIN_STEPS.length - 1;
                  const roundTop = isFirst ? "rounded-t-lg" : "";
                  const roundBottom = isLast ? "rounded-b-lg" : "";

                  if (isFirstIncomplete) {
                    return (
                      <div
                        key={step.number}
                        className={`relative z-10 flex flex-col gap-3 bg-brand-subtle border border-brand p-4 -mt-px first:mt-0 ${roundTop} ${roundBottom}`}
                      >
                        <div className="flex items-start gap-4">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-white text-sm font-medium leading-none">
                            {step.number}
                          </span>
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-semibold tracking-[-0.28px]">{step.title}</span>
                            <p className="text-[13px] text-secondary">{step.description}</p>
                          </div>
                        </div>
                        <Button
                          variant="brand"
                          className="ml-10 w-fit"
                          onClick={() => { handleStepClick(step); }}
                        >
                          {isStarted ? "Continue" : "Get started"}
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={step.number}
                      onClick={() => {
                        if (isCompleted) handleStepClick(step);
                      }}
                      className={`flex items-center gap-4 bg-white border border-primary p-4 -mt-px ${roundTop} ${roundBottom} ${isCompleted ? "cursor-pointer hover:bg-subtle/50" : ""}`}
                    >
                      {isCompleted ? (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
                          <Check className="h-4 w-4 text-white" />
                        </span>
                      ) : (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary text-sm font-medium">
                          {step.number}
                        </span>
                      )}
                      <span className={`flex-1 text-sm font-medium tracking-[-0.28px] ${isCompleted ? "text-tertiary" : ""}`}>
                        {step.title}
                      </span>
                      {isCompleted && (
                        <ChevronRight className="h-4 w-4 text-secondary" />
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <h3 className="font-semibold">Your checklist to getting authority</h3>
              </div>

              <div className="relative rounded-lg">
                <img
                  src={paywallBlurredBg}
                  alt=""
                  aria-hidden="true"
                  className="w-full pointer-events-none select-none"
                />
                <div className="absolute inset-0 z-10 flex justify-center pt-6 lg:pt-[50px] px-4 lg:px-0">
                  <div className="w-full max-w-[500px] rounded-lg border border-brand bg-white p-4 lg:p-6 flex flex-col gap-6 h-fit shadow-lg">
                    <div className="flex flex-col gap-3">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-subtle">
                        <Lock className="h-6 w-6 text-brand" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <h2>{CONTENT.paywallTitle}</h2>
                        <p className="text-sm text-secondary">
                          Everything you need to file with {countyLabel} Surrogate's Court, built
                          from what you confirmed.
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {CONTENT.paywallChecklist.filter((item) => helplineEnabled || !item.helpline).map((item) => (
                          <div key={item.text} className="flex items-center gap-2">
                            <Check className="h-5 w-5 shrink-0 text-brand" />
                            <span className="text-sm">{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-primary" />
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                          <span className="text-xl font-semibold">$500</span>
                          <span className="text-sm text-secondary">one time</span>
                        </div>
                        <span className="text-sm font-medium text-brand">
                          An eligible estate expense
                        </span>
                      </div>
                      <Button variant="brand" rounded onClick={() => setPaywallOpen(true)}>
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-secondary text-center">
                      Pay from the estate, or pay now and reimburse yourself.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="w-full lg:w-[300px] shrink-0 flex flex-col gap-8">
          {isPaid ? (
            <>
              {!isFirstVisit && (
                <AuthorityResultsCard
                  heading={CONTENT.heading(decedentFirstName)}
                  description={CONTENT.sidebarDescription(decedentFirstName)}
                />
              )}

              <KeyDetailsCard
                county={county}
                timeline={CONTENT.timeline}
                filingFee={CONTENT.filingFee}
                attorneyStatus={CONTENT.attorneyStatus}
              />

              <EstateExpertWidget
                onSeePlans={() => setPaywallOpen(true)}
              />
            </>
          ) : (
            <>
              <EstateExpertWidget
                onSeePlans={() => setPaywallOpen(true)}
              />

              <div className="flex flex-col gap-3">
                <h3>What we know</h3>
                <div className="rounded-lg border border-primary overflow-hidden shadow-sm">
                  <KnowRow
                    icon={Users}
                    label="Family map"
                    value={`${familyCount} ${familyCount === 1 ? "person" : "people"}`}
                  />
                  <KnowRow
                    icon={Home}
                    label="Property"
                    value={`${propertyCount} confirmed`}
                  />
                  <KnowRow
                    icon={Lock}
                    label="Will"
                    value="No will"
                  />
                  <KnowRow
                    icon={FileText}
                    label="Documents"
                    value={`${keyDocCount} of ${keyDocTotal} key docs`}
                  />
                  <KnowRow
                    icon={Gavel}
                    label="Attorney"
                    value={attorneyName ?? "Not added"}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <PostLossPaywall
        isOpen={paywallOpen}
        onOpenChange={setPaywallOpen}
        promptLocation="get_authorized"
        currentTier={tier}
        returnPath={POST_LOSS_ROUTES.GET_AUTHORIZED}
      />

      <Sheet open={!!activeFlow} onOpenChange={(open) => {
        if (!open) handleCloseSheet();
      }}>
        <SheetContent
          side="right"
          showClose={false}
          overlayClassName="bg-black/30 backdrop-blur-[2px]"
          className="w-full sm:max-w-none sm:w-[50vw] p-0 flex flex-col border-l border-primary shadow-[0_0_24px_0_rgba(0,0,0,0.09)] bg-white"
        >
          <div className="flex flex-col gap-3 border-b border-primary p-6 shrink-0">
            <div className="flex items-center gap-3 w-full">
              <span className="text-base font-medium tracking-[-0.02em] text-secondary shrink-0">
                Step {flowStep + 1}
              </span>
              <span className="h-1 w-1 rounded-full bg-tertiary shrink-0" aria-hidden="true" />
              <span className="text-base font-medium tracking-[-0.02em] text-secondary flex-1 min-w-0 truncate">
                {(currentScreen?.id && ADMIN_SCREEN_TITLES[currentScreen.id])
                  ? ADMIN_SCREEN_TITLES[currentScreen.id]
                  : (activeStepConfig?.title ?? "")}
              </span>
              <button
                onClick={handleCloseSheet}
                className="flex h-5 w-5 shrink-0 items-center justify-center hover:opacity-70"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-4 w-full">
              <div className="relative flex-1 h-2 rounded-full bg-background overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-brand transition-all duration-300"
                  style={{
                    width: `${flowTotal > 0 ? ((flowStep + 1) / flowTotal) * 100 : 0}%`,
                  }}
                />
              </div>
              <span className="text-sm text-secondary shrink-0 whitespace-nowrap">
                {flowStep + 1} of {flowTotal}
              </span>
            </div>
          </div>

          <SheetTitle className="sr-only">
            {activeStepConfig?.title ?? "Step"}
          </SheetTitle>

          <div ref={sheetContentRef} className="flex-1 min-h-0 overflow-y-auto bg-white p-4 sm:p-8">
            {currentScreen && (
              <div className="flex flex-col gap-6">
                {currentScreen.customComponent ? (
                  <currentScreen.customComponent
                    onComplete={() => flowGoNext()}
                    answers={flowAnswers}
                    setAnswer={setFlowAnswer}
                    goToScreen={flowGoToScreen}
                    errors={flowErrors}
                  />
                ) : (
                  <>
                    {(currentScreen.title || currentScreen.subtitle) && (
                      <div className="flex flex-col gap-1">
                        {currentScreen.title && (
                          <p className="font-sans text-xl font-semibold leading-[30px]">
                            {currentScreen.title}
                          </p>
                        )}
                        {currentScreen.subtitle && (
                          <p className="text-sm text-secondary tracking-[-0.28px]">
                            {currentScreen.subtitle}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-primary p-6 shrink-0">
            {flowStep > 0 ? (
              <Button variant="outline" onClick={() => flowGoBack()}>
                Back
              </Button>
            ) : (
              <div />
            )}
            {!(typeof currentScreen?.hideButton === "function"
              ? currentScreen.hideButton({ answers: flowAnswers, currentStep: flowStep })
              : currentScreen?.hideButton) && (
              <Button
                variant={isNextDisabled || isSaving ? "outline" : "brand"}
                disabled={isNextDisabled || isSaving}
                onClick={() => flowGoNext()}
                className={isNextDisabled && !isSaving ? "bg-background text-disabled border-0" : ""}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    {currentScreen?.getButtonText?.({ answers: flowAnswers, currentStep: flowStep }) ??
                      (flowIsLast ? "Done" : "Next")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MainLayout>
  );
};
